import {
  BooleanInput,
  DeleteWithConfirmButton,
  Edit,
  NumberInput,
  ReferenceInput,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  usePermissions,
  useRecordContext,
  TabbedForm,
  FormTab,
} from 'react-admin'
import { Box, Typography } from '@mui/material'
import { useState, useEffect } from 'react'
import userPermissions from '../Users/userPermissions'
import OrganisationVendorFormDirectory from './OrganisationVendorFormDirectory'
import OrganisationApiKeys from './OrganisationApiKeys'

const EditToolbar = (props: any) => {
  const record = useRecordContext()

  return (
    <Toolbar {...props}>
      <SaveButton />
      <DeleteWithConfirmButton
        confirmContent="You will not be able to recover this record. Are you sure?"
        translateOptions={{ name: record.name }}
      />
    </Toolbar>
  )
}

const OrganisationEdit = () => {
  const { permissions } = usePermissions()
  const record = useRecordContext()
  // Removed showRiskTab state and useEffect as the Questionnaires tab has been removed entirely
  // since this functionality is now handled elsewhere

  // Keep debugging logs for reference
  console.log('OrganisationEdit - record:', record)
  console.log('OrganisationEdit - record?.show_risk:', record?.show_risk)
  console.log(
    'OrganisationEdit - typeof record?.show_risk:',
    typeof record?.show_risk
  )

  const transform = (data) => {
    try {
      data.import_config = JSON.stringify(data.import_config)
      if (data.risk_config) {
        data.risk_config = JSON.stringify(data.risk_config)
      }
      return data
    } catch (e) {
      console.log(e)
      return data
    }
  }

  return (
    <Edit transform={transform}>
      <TabbedForm toolbar={<EditToolbar />}>
        <FormTab label="General">
          <TextInput source="name" />
          <TextInput source="price_field" />
          <TextInput
            source="import_config"
            format={(v) => {
              return JSON.stringify(v)
            }}
            parse={(v) => {
              return JSON.parse(v)
            }}
            multiline
            fullWidth
          />
          <TextInput
            source="risk_config"
            format={(v) => {
              return v ? JSON.stringify(v) : ''
            }}
            parse={(v) => {
              return v ? JSON.parse(v) : null
            }}
            multiline
            fullWidth
          />
          <BooleanInput source="show_risk" label="Show Risk" />
          <BooleanInput source="show_contracts" label="Show Contracts" />
          <BooleanInput source="show_ml" label="Show ML Predictions" />

          {/* 2FA Organisation Settings */}
          {permissions & userPermissions.SUPER_ADMIN ? (
            <>
              <BooleanInput
                source="twofa_default_new_users"
                label="Enable 2FA for new users"
                helperText="When enabled, all newly created users in this organisation will have 2FA enabled by default."
              />
              <BooleanInput
                source="twofa_enforce_all_users"
                label="Enable 2FA for all users"
                helperText="Toggle to enable or disable 2FA for all users in this organisation. Toggling will reset app secrets so users will need to (re)setup if enabled."
              />
            </>
          ) : null}

          <ReferenceInput source="currency_id" reference="currencies">
            <SelectInput label="Currency" optionText="name" optionValue="id" />
          </ReferenceInput>
          <TextInput source="groups_report_extra_tags" />
          {permissions & userPermissions.SUPER_ADMIN ? (
            <BooleanInput source="is_reference" />
          ) : null}
          {permissions & userPermissions.SUPER_ADMIN ? (
            <SelectInput
              source="noun"
              choices={[
                { id: 'CLAIM', name: 'CLAIM' },
                { id: 'SPEND', name: 'SPEND' },
              ]}
            />
          ) : null}
          {permissions & userPermissions.SUPER_ADMIN ? (
            <NumberInput source="priority" />
          ) : null}
        </FormTab>
        <FormTab label="Vendor Forms">
          <Box p={2}>
            <Typography variant="h6" gutterBottom>
              Vendor Forms Configuration
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Manage vendor forms that will be used when creating new vendors.
              Create, edit, and delete forms to customize the information
              collected.
            </Typography>
            <OrganisationVendorFormDirectory />
          </Box>
        </FormTab>
        {permissions & userPermissions.SUPER_ADMIN ? (
          <FormTab label="API Keys">
            <OrganisationApiKeys />
          </FormTab>
        ) : null}
      </TabbedForm>
    </Edit>
  )
}

export default OrganisationEdit
